import { FontFamily } from '@euler/components/typography/fonts';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import React, { memo, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { CartesianChartPayload, ChartSeries } from './payloads';

/** Whether a cartesian chart draws its legend. */
export function showChartLegend(data: CartesianChartPayload): boolean {
  return data.legend ?? (data.series ?? []).length > 1;
}

export const CHART_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
];

export function useChartStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        chartContainer: {
          backgroundColor: '#F8FAFC',
          borderRadius: rpx(10),
          padding: rpx(12),
          marginVertical: rpx(6),
          // Hard guarantee the drawn chart never spills past the card edge.
          overflow: 'hidden',
        },
        chartTitle: {
          fontSize: rpx(13),
          fontFamily: FontFamily.NotoSans.Bold,
          color: '#1E293B',
          marginBottom: rpx(8),
        },
        chartAxisText: {
          fontSize: rpx(9),
          fontFamily: FontFamily.NotoSans.Regular,
          color: '#94A3B8',
        },
        chartLegend: {
          flexDirection: 'row' as const,
          flexWrap: 'wrap' as const,
          marginTop: rpx(8),
          gap: rpx(12),
        },
        chartLegendItem: {
          flexDirection: 'row' as const,
          alignItems: 'center' as const,
        },
        chartLegendDot: {
          width: rpx(8),
          height: rpx(8),
          borderRadius: rpx(4),
          marginRight: rpx(4),
        },
        chartLegendText: {
          fontSize: rpx(11),
          fontFamily: FontFamily.NotoSans.Regular,
          color: '#64748B',
        },
        donutCenterText: {
          fontSize: rpx(14),
          fontFamily: FontFamily.NotoSans.Bold,
          color: '#1E293B',
        },
      }),
    [rpx],
  );
}

export const ChartLegend = memo(({ series }: { series: ChartSeries[] }) => {
  const s = useChartStyles();
  return (
    <View style={s.chartLegend}>
      {series.map((sr, i) => (
        <View key={i} style={s.chartLegendItem}>
          <View
            style={[
              s.chartLegendDot,
              {
                backgroundColor:
                  sr.color ?? CHART_COLORS[i % CHART_COLORS.length],
              },
            ]}
          />
          <Text style={s.chartLegendText}>{sr.name}</Text>
        </View>
      ))}
    </View>
  );
});
